import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import PageHeader from "designV2/components/PageHeader";
import Panel from "designV2/components/Panel";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import StatusBadge from "designV2/components/StatusBadge";
import { Skeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const GET_ROLES = gql`
  query rolesForAdminForm {
    roles(limit: 100, skip: 0) {
      id
      name
      description
    }
  }
`;

const GET_SHOPS = gql`
  query getShopsForAdminForm($name: String) {
    getShopsByAdmin(pagination: { limit: 20, skip: 0 }, filters: { name: $name }) {
      _id
      name
    }
  }
`;

const CREATE_ADMIN = gql`
  mutation createAdminByAdmin(
    $fullName: String!
    $email: String!
    $phoneNumber: String!
    $password: String!
    $state: AdminState
    $type: String
    $shop: ID
    $roles: [ID!]!
  ) {
    createAdminByAdmin(
      input: {
        fullName: $fullName
        email: $email
        phoneNumber: $phoneNumber
        password: $password
        state: $state
        type: $type
        shop: $shop
        roles: $roles
      }
    ) {
      _id
      fullName
    }
  }
`;

const UPDATE_ADMIN = gql`
  mutation updateAdminByAdmin(
    $id: ID!
    $fullName: String
    $email: String
    $phoneNumber: String
    $state: AdminState
    $type: String
    $shop: ID
    $roles: [ID!]!
  ) {
    updateAdminByAdmin(
      adminId: $id
      input: {
        fullName: $fullName
        email: $email
        phoneNumber: $phoneNumber
        state: $state
        type: $type
        shop: $shop
        roles: $roles
      }
    ) {
      _id
      fullName
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const TYPE_OPTIONS = [
  { value: "SUPER-ADMIN", label: "Super admin" },
  { value: "SHOP-ADMIN", label: "Shop admin" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_DEBOUNCE_MS = 350;

/**
 * AddAdmin - create or edit an admin account. EditAdmin passes one in.
 *
 * `roles` is [ID!]! on both mutations, so at least one role is required;
 * the previous form let you submit with none selected and surfaced the
 * server's rejection as an untitled modal. A shop admin also needs a
 * shop — that is enforced here rather than producing an account that
 * cannot sign in anywhere.
 */
const AddAdmin = ({ admin }) => {
  const isEdit = Boolean(admin);

  const [fullName, setFullName] = useState(admin ? admin.fullName || "" : "");
  const [email, setEmail] = useState(admin ? admin.email || "" : "");
  const [phoneNumber, setPhoneNumber] = useState(admin ? admin.phoneNumber || "" : "");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [type, setType] = useState(admin ? admin.type || "SUPER-ADMIN" : "SUPER-ADMIN");
  const [state, setState] = useState(admin ? admin.state || "ACTIVE" : "ACTIVE");
  const [roleIds, setRoleIds] = useState(
    admin && admin.roles ? admin.roles.map((role) => role.id).filter(Boolean) : []
  );
  const [shopId, setShopId] = useState(admin && admin.shop ? admin.shop._id : "");
  const [shopSearch, setShopSearch] = useState("");

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES, { errorPolicy: "all" });
  const [findShops, { data: shopsData }] = useLazyQuery(GET_SHOPS, { fetchPolicy: "network-only" });
  const [createAdmin] = useMutation(CREATE_ADMIN);
  const [updateAdmin] = useMutation(UPDATE_ADMIN);

  const isShopAdmin = type === "SHOP-ADMIN";

  useEffect(() => {
    if (!isShopAdmin) return undefined;
    const handler = setTimeout(() => {
      findShops({ variables: { name: shopSearch.trim() } });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [isShopAdmin, shopSearch, findShops]);

  const roles = (rolesData && rolesData.roles) || [];
  const shopOptions = useMemo(
    () => ((shopsData && shopsData.getShopsByAdmin) || []).map((s) => ({ value: s._id, label: s.name })),
    [shopsData]
  );
  const selectedShopLabel =
    (shopOptions.find((o) => o.value === shopId) || {}).label ||
    (admin && admin.shop && admin.shop._id === shopId ? admin.shop.name : "");

  const toggleRole = (id) =>
    setRoleIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const validate = () => {
    const next = {};
    if (!fullName.trim()) next.fullName = "A name is required.";
    if (!email.trim()) next.email = "An email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(email.trim())) next.email = "That does not look like an email.";
    if (!phoneNumber.trim()) next.phoneNumber = "A phone number is required.";
    if (!isEdit && !password) next.password = "Set a password for the new account.";
    else if (!isEdit && password.length < 8) next.password = "Use at least 8 characters.";
    if (!roleIds.length) next.roles = "Pick at least one role.";
    if (isShopAdmin && !shopId) next.shop = "A shop admin needs a shop.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (event) => {
    if (event) event.preventDefault();
    if (saving) return;
    setFormError("");
    if (!validate()) return;

    const common = {
      fullName: fullName.trim(),
      email: email.trim(),
      phoneNumber: phoneNumber.trim(),
      state,
      type,
      shop: isShopAdmin ? shopId : undefined,
      roles: roleIds,
    };

    setSaving(true);
    const run = isEdit
      ? updateAdmin({ variables: { ...common, id: admin._id } })
      : createAdmin({ variables: { ...common, password } });

    run
      .then(() => {
        window.location.href = `${endPoint}/superAdmin/admins`;
      })
      .catch((err) => {
        setSaving(false);
        setFormError(readableError(err));
      });
  };

  return (
    <div className="page superPeoplePage">
      <main className="main">
        <PageHeader
          title={isEdit ? "Edit admin" : "New admin"}
          description={
            isEdit
              ? admin.fullName || ""
              : "Give someone access to the super admin or a shop admin panel."
          }
          actions={
            <StatusBadge
              status={state === "SUSPENDED" ? "danger" : "success"}
              label={STATE_LABELS[state] || state}
            />
          }
        />

        <form onSubmit={onSubmit}>
          <Panel title="Account">
            <div className="superPersonForm">
              {formError && (
                <div className="dsFormMessage error" role="alert">
                  {formError}
                </div>
              )}

              <Field label="Full name" htmlFor="fullName" error={errors.fullName} required>
                <input
                  id="fullName"
                  className="dsInput"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </Field>

              <div className="superPersonFormRow">
                <Field label="Email" htmlFor="email" error={errors.email} required>
                  <input
                    id="email"
                    className="dsInput"
                    type="email"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>

                <Field label="Phone number" htmlFor="phoneNumber" error={errors.phoneNumber} required>
                  <input
                    id="phoneNumber"
                    className="dsInput"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </Field>
              </div>

              {!isEdit && (
                <Field
                  label="Password"
                  htmlFor="password"
                  error={errors.password}
                  required
                  hint="The new admin signs in with this. Share it with them separately."
                >
                  <span className="superPersonValueRow">
                    <input
                      id="password"
                      className="dsInput"
                      type={passwordVisible ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPasswordVisible((current) => !current)}
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                    >
                      {passwordVisible ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </Button>
                  </span>
                </Field>
              )}

              {isEdit && (
                <div className="dsFormMessage info" role="status">
                  Passwords cannot be changed here — the admin resets their own from the sign-in
                  screen.
                </div>
              )}
            </div>
          </Panel>

          <Panel title="Access">
            <div className="superPersonForm">
              <div className="superPersonFormRow">
                <Field label="Type" hint="Which panel this account signs in to.">
                  <FilterDropdown
                    label={TYPE_LABELS[type] || type}
                    options={TYPE_OPTIONS}
                    value={type}
                    active={false}
                    onChange={(e) => setType(e.target.value)}
                  />
                </Field>

                <Field label="State" hint="Suspending blocks sign-in immediately.">
                  <FilterDropdown
                    label={STATE_LABELS[state] || state}
                    options={STATE_OPTIONS}
                    value={state}
                    active={false}
                    onChange={(e) => setState(e.target.value)}
                  />
                </Field>
              </div>

              {isShopAdmin && (
                <React.Fragment>
                  <Field
                    label="Shop"
                    error={errors.shop}
                    required
                    hint="Search by name, then pick the shop this admin manages."
                  >
                    <SearchField
                      placeholder="Search shops by name..."
                      value={shopSearch}
                      onChange={(e) => setShopSearch(e.target.value)}
                    />
                  </Field>
                  <FilterDropdown
                    label={selectedShopLabel ? `Shop: ${selectedShopLabel}` : "Choose a shop"}
                    options={
                      shopOptions.length
                        ? shopOptions
                        : [{ value: "", label: "No matches — try another name" }]
                    }
                    value={shopId}
                    onChange={(e) => setShopId(e.target.value)}
                  />
                </React.Fragment>
              )}
            </div>
          </Panel>

          <Panel
            title="Roles"
            subtitle="Roles decide which screens and actions this admin can reach."
          >
            {rolesLoading ? (
              <Skeleton height={120} />
            ) : roles.length === 0 ? (
              <span className="superPersonMuted">No roles are defined yet.</span>
            ) : (
              <div className="superPersonForm">
                {roles.map((role) => (
                  <label
                    className={`dsOptionCard ${roleIds.includes(role.id) ? "isSelected" : ""}`}
                    key={role.id}
                  >
                    <input
                      type="checkbox"
                      checked={roleIds.includes(role.id)}
                      onChange={() => toggleRole(role.id)}
                    />
                    <span className="dsOptionCardText">
                      <strong>{role.name}</strong>
                      <span>{role.description}</span>
                    </span>
                  </label>
                ))}
                {errors.roles && (
                  <span className="dsFieldError" role="alert">
                    {errors.roles}
                  </span>
                )}
              </div>
            )}
          </Panel>

          <div className="superPersonFooter">
            <Button
              variant="secondary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/admins`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              {isEdit ? "Save changes" : "Create admin"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AddAdmin;
